import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { AdminService } from '../admin.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';


@Component({
    selector: 'gtw-data-push-to-efile',
    templateUrl: './data-push-to-efile.component.html',
    styleUrls: ['./data-push-to-efile.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})


export class DataPushToEfileComponent implements OnInit, OnDestroy {

  private readonly subs = new Subscription();
  private selectedGridRows: any[] = [];

  constructor(private adminService : AdminService,
    private AlertService : GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) { }

  @Output('cancel-data-push-confirmation') cancelDataPushConfirmation: EventEmitter<any> = new EventEmitter<any>();
  @Output('request-grid-update') requestGridUpdate: EventEmitter<string> = new EventEmitter<string>();

  loading: boolean  = false;
  confirmConfig: ConfirmationModalConfig = {
    title: 'Confirm Refresh',
    message: 'Are you sure you want to push DST data to Efile?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
  };
  filterParams: any = {};
  globalService: any = {};

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
    this.setConfirmLoading(false);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Legacy source values (DSTDataPushToEfileController):
   * - taxYear/dcnId/submissionId: workspaceFactory.activeScreen.filters.filterParams
   * - gridData: selected grid rows transformed into statement payload array
   *
   * In the migrated flow, CeHostDialog does not pass a `dataPushInput`
   * attribute, so read the real values from the workspace activeScreen
   * (filters + selected rows) instead of relying on a host-supplied input.
   */
  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.selectedGridRows = this.extractSelectedGridRows(activeScreen);
  }

  private extractSelectedGridRows(activeScreen: any): any[] {
    const dataGridRows = activeScreen?.data?.gridData;
    if (Array.isArray(dataGridRows) && dataGridRows.length > 0) {
      return dataGridRows;
    }

    // Fallbacks used by various grid integrations/workspace snapshots.
    const selectedRows =
      activeScreen?.selectedRows
      || activeScreen?.selected?.rows
      || activeScreen?.data?.selectedRows
      || activeScreen?.data?.selected?.rows;

    if (Array.isArray(selectedRows) && selectedRows.length > 0) {
      return selectedRows;
    }

    return [];
  }

  private buildGridDataPayload(): string {
    const extractedData = (this.selectedGridRows || []).map((row: any) => {
      const src = row?.data || row || {};
      return {
        statementId: src.STATEMENT_ID ?? src.statementId,
      };
    }).filter((x: any) => x.statementId != null && x.statementId !== '');

    return JSON.stringify(extractedData);
  }

  refresh():void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.setConfirmLoading(true);
    this.AlertService.openSnackBar('Data Pushed to Efile is initiated...', 'gtw-snackbar--green');
    firstValueFrom(this.adminService.refreshDSTDataOnEfile(
      this.filterParams?.tax_year,
      this.filterParams?.dcn_id,
      this.filterParams?.submission_id,
      this.buildGridDataPayload()
    )).then(
      (response : any) => {
          if (response && response.callSuccess === '1') {
              this.AlertService.openSnackBar('Data Pushed to Efile Successfully!', 'gtw-snackbar--green');
              this.loading = false;
              this.setConfirmLoading(false);
              this.emitRefresh();

          } else {
              throw new Error(response.errorMessage);

          }
      })
      .catch((err : any) => {
          this.emitCancel();
          this.AlertService.openSnackBar(err.message === undefined ?'Something went wrong, please try again!': err.message, 'gtw-snackbar--red');
          this.loading = false;
          this.setConfirmLoading(false);

      }
  );

  }

  cancel () : void {
    this.loading = false;
    this.setConfirmLoading(false);
    this.emitCancel();
  };

  private setConfirmLoading(loading: boolean): void {
    this.confirmConfig = { ...this.confirmConfig, loading };
  }

  /**
   * Legacy AngularJS host listens to `cancel-data-push-confirmation`; migrated
   * CeHostDialog listens to `request-grid-update` ("cancel"/"refresh"). Emit
   * both so the modal closes correctly in both hosts.
   */
  private emitCancel(): void {
    this.cancelDataPushConfirmation.emit();
    this.requestGridUpdate.emit('cancel');
  }

  private emitRefresh(): void {
    this.cancelDataPushConfirmation.emit();
    this.requestGridUpdate.emit('refresh');
  }

}
